// Einheitlicher fetch-Wrapper fuer /api/*.
// Alle Responses folgen { ok: true, ... } | { ok: false, error: string }.
// Bei ok:false oder Nicht-2xx-HTTP wird eine ApiError geworfen.

import { verlangeReauth } from './reauth';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /**
     * Was der Server dem Fehler beigelegt hat.
     *
     * Manche Ablehnungen sind keine Sackgasse, sondern eine Rueckfrage:
     * `mehrere_wege_offen` bringt die Liste der offenen Beschaffungswege
     * mit, damit die Oberflaeche "von wem?" ohne zweiten Abruf fragen
     * kann. Ohne dieses Feld ginge die Liste beim Werfen verloren und die
     * Oberflaeche muesste nachladen, was sie schon hatte.
     */
    public daten: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type OkPayload<T> = { ok: true } & T;
type ErrPayload = { ok: false; error: string; message?: string } & Record<string, unknown>;
type ApiResponse<T> = OkPayload<T> | ErrPayload;

// Auth-Endpoints sind vom Reauth-Ablauf ausgenommen: ein 401 von
// /auth/me.php ist das normale Signal "nicht angemeldet" beim ersten
// Seitenaufruf und darf keinen Dialog ausloesen (S-1).
const OHNE_REAUTH = ['/auth/me.php', '/auth/login.php', '/auth/callback.php'];

function rohFetch(path: string, init: RequestInit): Promise<Response> {
  return fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...init.headers
    }
  });
}

async function auswerten<T>(res: Response): Promise<T> {
  let data: ApiResponse<T> | Record<string, never> = {};
  try {
    data = (await res.json()) as ApiResponse<T>;
  } catch {
    // Nicht-JSON-Body: leeres data, wird unten als http_error klassifiziert.
  }

  if (!res.ok) {
    const err = data as ErrPayload;
    const code = err.error ?? 'http_error';
    // Serverseitigen Klartext bevorzugen, falls vorhanden.
    throw new ApiError(res.status, code, err.message ?? `${res.status} ${code}`, err);
  }
  if ('ok' in data && data.ok === false) {
    throw new ApiError(res.status, data.error, data.message ?? data.error, data);
  }
  if (!('ok' in data)) {
    throw new ApiError(res.status, 'invalid_response', 'Response ohne ok-Feld');
  }
  // ok ist true — strip the ok-Feld und gib den Payload zurueck.
  const { ok: _ok, ...rest } = data;
  return rest as T;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await rohFetch(path, init);

  // Abgelaufene Sitzung: nicht hart auf /login umleiten, sondern neu
  // anmelden lassen und die Anfrage genau einmal wiederholen. Der
  // aufrufende View bleibt dabei stehen, samt Eingaben (S-1).
  if (res.status === 401 && !OHNE_REAUTH.includes(path.split('?')[0])) {
    const wiederAngemeldet = await verlangeReauth();
    if (wiederAngemeldet) {
      return auswerten<T>(await rohFetch(path, init));
    }
  }

  return auswerten<T>(res);
}
